/**
 * The product this bundle is (or may be) running as.
 * Contract: docs/releases/frontend-products.md
 *
 * Two products share this codebase — Hallouminati and GreekBrain — and share
 * one backend and one user. Everything that differs between them as a *value*
 * (name, hosts, analytics keys, bundle id, mascot, what "pro" means) lives in
 * PRODUCTS below, so a component reads `product.mascot` instead of asking
 * `appType === 'hallouminati'` and hard-coding both answers inline.
 *
 * Behavioural differences (a feature that exists in one product only) still
 * branch on `appType`; that is a legitimate use. What this module removes is
 * the duplicated *lookup table* that used to be spread across ~50 files.
 *
 * ## Build-time vs runtime product
 *
 * `VITE_APP=hallouminati|greekbrain` at build time pins the product:
 * `BUILT_PRODUCT` is a literal after Vite's define step, so every
 * `BUILT_PRODUCT === 'x'` folds to a constant and the other product's route
 * tree (a dynamic import in App.tsx) is never emitted. Hosts, native flavours
 * and OTA bundles are then per product — see docs/releases/ios-ota-releases.md.
 *
 * Without `VITE_APP` (the dev server, the legacy "universal" bundle) the
 * product is resolved at runtime from the hostname / path / native bundle id,
 * exactly as before — see utils/webAppType.ts and services/platform.ts.
 */
import type { AppType, User } from '@/types';
import { COLORS } from '@/theme/colors';
import hallouminatiLogo from '@/assets/img/hallouminati/loading_logo.jpg';
import greekBrainLogo from '@/assets/img/greekbrain/greeky_generic_logo.png';
import greekbrainMascot from '@/assets/img/loading_mascote.png';

export type ProductId = AppType;

export const PRODUCT_IDS: readonly ProductId[] = ['hallouminati', 'greekbrain'] as const;

export function isProductId(value: unknown): value is ProductId {
    return value === 'hallouminati' || value === 'greekbrain';
}

/**
 * The product this bundle was built for, or null for a universal bundle.
 * Validated so a typo in CI fails the build loudly instead of shipping a
 * bundle that thinks it is Hallouminati.
 */
export const BUILT_PRODUCT: ProductId | null = (() => {
    const raw = import.meta.env.VITE_APP as string | undefined;
    if (!raw) return null;
    if (!isProductId(raw)) {
        throw new Error(`VITE_APP must be one of ${PRODUCT_IDS.join(', ')}; got "${raw}"`);
    }
    return raw;
})();

export interface ProductConfig {
    id: ProductId;
    /** Display name, also the document title prefix. */
    name: string;
    /** Marketing tagline (document title on the landing/login). */
    title: string;
    description: string;
    themeColor: string;

    /** Marketing site, no trailing slash. */
    marketingUrl: string;
    /** The web app, no trailing slash. Also the Telegram Mini App host. */
    appUrl: string;
    /** Terms / privacy pages. Both products are hosted on hallouminati.app today. */
    legalUrl: (page: 'terms' | 'privacy') => string;

    /** iOS bundle id; also the Sign in with Apple clientId. */
    iosBundleId: string;
    /** Custom URL scheme for native OAuth callbacks (`<scheme>://oauth/...`). */
    urlScheme: string;

    posthogKey: string | undefined;
    ripplesToken: string | undefined;
    /** Public by design — a DSN only grants "send events to this project". */
    sentryDsn: string;

    logo: string;
    /** Loading-screen mascot and background. */
    mascot: string;
    loadingBackground: string;
    /** Rotating loading copy; empty means "show the caller's message". */
    loadingMessages: readonly string[];

    /** Label on the paid badge: lifetime "ADEPT" vs monthly "PRO". */
    proLabel: string;
    /** Whether this user has paid for *this* product. */
    isPro: (user: Pick<User, 'is_pro' | 'is_greekbrain_subscribed'>) => boolean;
}

export const PRODUCTS: Record<ProductId, ProductConfig> = {
    hallouminati: {
        id: 'hallouminati',
        name: 'Hallouminati',
        title: 'Pass your Cyprus Culture Exam on the First Try',
        description: 'Learn Cyprus history and culture, train your vocabulary, and practice with real exam questions. Everything you need to ace the exam — all in one app.',
        themeColor: COLORS.info,
        marketingUrl: 'https://hallouminati.app',
        appUrl: 'https://app.hallouminati.app',
        legalUrl: (page) => `https://hallouminati.app/legal/hallouminati/${page}`,
        iosBundleId: 'com.konura.hallouminati',
        urlScheme: 'hallouminati',
        posthogKey: import.meta.env.VITE_POSTHOG_ID_HALLOUMINATI,
        ripplesToken: import.meta.env.VITE_RIPPLES_TOKEN_HALLOUMINATI,
        sentryDsn: 'https://2a6cca62d6b24f39713793515e396d24@sentry.konura.com/13',
        logo: hallouminatiLogo,
        mascot: hallouminatiLogo,
        // The session ground: eggshell in light, the native splash navy in dark
        // (docs/design/dark-mode-plan.md §4), so a dark launch never changes colour.
        loadingBackground: 'var(--color-ground-session)',
        loadingMessages: [
            'Consulting the Oracle of Halloumi...',
            'Aligning the curds...',
            'Unfolding the prophecy...',
            'Preparing the wisdom...',
            'Grilling the data...',
        ],
        proLabel: 'ADEPT',
        isPro: (user) => !!user.is_pro,
    },
    greekbrain: {
        id: 'greekbrain',
        name: 'GreekBrain',
        title: 'Greek Language learning app',
        description: 'Learn Greek vocabulary effortlessly with personalized micro-lessons that adapt to your pace, helping you master new words and retain them long-term through smart practice sessions.',
        themeColor: COLORS.primary,
        marketingUrl: 'https://greekbrain.app',
        appUrl: 'https://app.greekbrain.app',
        legalUrl: (page) => `https://hallouminati.app/legal/greekbrain/${page}`,
        iosBundleId: 'app.greekbrain',
        urlScheme: 'greekbrain',
        posthogKey: import.meta.env.VITE_POSTHOG_ID_GREEKBRAIN,
        ripplesToken: import.meta.env.VITE_RIPPLES_TOKEN_GREEKBRAIN,
        sentryDsn: 'https://eb7cad700a527362357c493193ebce5b@sentry.konura.com/28',
        logo: greekBrainLogo,
        mascot: greekbrainMascot,
        loadingBackground: '#D4EBFC',
        loadingMessages: [],
        proLabel: 'PRO',
        isPro: (user) => !!user.is_greekbrain_subscribed,
    },
};

export function getProduct(id: ProductId): ProductConfig {
    return PRODUCTS[id];
}

/** The other product — where a cross-product link on a pinned build should go. */
export function otherProduct(id: ProductId): ProductConfig {
    return PRODUCTS[id === 'hallouminati' ? 'greekbrain' : 'hallouminati'];
}
